import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes, { number, string } from 'prop-types';

import LineTitle from '../atoms/titles/LineTitle';
import Textlink from '../atoms/link/Textlink';
import LinkButton from '../atoms/button/LinkButton';
import RoomStateLabel from '../atoms/label/Label';
import Tag from '../atoms/tag/Tag';
import PriceBlock from '../molecules/PriceBlock';
import { makeAdditionalParamsPlanDetail, makeQueryStringsWithDuplicatedSessionKey } from '../../utils/queryParams';
import { BASENAME, SHOWING_PLAN_LIMIT } from '../../constants';
import { usePrice } from '../../utils/price';

const FEMALE_TAG_ID = 'female_only';

const selector = (state) => ({
  flight: state.flight,
  planDetail: state.planDetail,
});

const RecommendPlanList = (props) => {
  const {
    handlePlanSelect,
    handleClickPlanDetailLink,
    plans,
    planListLink,
    handleClickPlanListLink,
    planStartDate,
    maxPlanDisplayNumber,
  } = props;

  const { flight, planDetail } = useSelector(selector);
  const {
    searchedFlightsInput, selectedOutboundFlight, selectedInboundFlight,
    fetchingOutbound, fetchingInbound, selectedOutboundDate
  } = flight;
  const people = searchedFlightsInput && searchedFlightsInput.people;

  const { plans: selectedPlans } = planDetail;

  const showingPlanLimit = maxPlanDisplayNumber || SHOWING_PLAN_LIMIT;

  const showingPlans = plans ? plans.slice(0, showingPlanLimit) : [];

  const hidePrice = fetchingOutbound || fetchingInbound;
  const priceClassName = hidePrice ? 'hidden' : '';

  return (
    <div className="RecommendPlanList">
      <LineTitle text="おすすめプラン" />
      {showingPlans.map((plan, index) => {
        const {
          id,
          // tieupCompID,
          name,
          stocks,
          recommendationPoint,
          roomType,
          hotel,
          tags: planTags,
          femaleOnlyPlan,
          mealCondition,
        } = plan;

        const { id: roomTypeId } = roomType;

        const roomStateLittle = stocks < 7;
        const roomStateLabelColor = roomStateLittle ? 'red' : 'gray';
        const roomStateLabelText = roomStateLittle ? '空室わずか' : '空室あり';

        const planDetailParams = makeAdditionalParamsPlanDetail(id, roomTypeId);
        const search = makeQueryStringsWithDuplicatedSessionKey(planDetailParams);
        const planDetailLink = `${BASENAME}/plan_detail?${search}`;

        const handleButton = () => {
          if (handlePlanSelect) {
            return handlePlanSelect(plan);
          }

          const searchConfirm = makeQueryStringsWithDuplicatedSessionKey(planDetailParams);
          const entryConfirmLink = `${BASENAME}/entry_confirm?${searchConfirm}`;
          window.open(entryConfirmLink, '_blank');
        };

        const { tags: hotelTags } = hotel;

        const tags = [...hotelTags, ...planTags];

        if (femaleOnlyPlan) {
          const femaleTag = {
            id: FEMALE_TAG_ID,
            name: '女性限定',
            tagGroup: {},
          };

          tags.push(femaleTag);
        }

        const { totalPrice, pricePerAdult } = usePrice(
          people,
          selectedOutboundFlight,
          selectedInboundFlight,
          selectedPlans.length ? [
            ...selectedPlans.map(p => {
              if (p.startDate === planStartDate) {
                return undefined;
              }

              return p.plan;
            }).filter(Boolean),
            plan,
          ] : [plan],
          selectedOutboundDate,
        );

        return (
          <div className="RecommendPlan" key={`${id}-${roomTypeId}-${index}`}>
            <div className="title">
              {handleClickPlanDetailLink ? (
                <a onClick={() => handleClickPlanDetailLink(plan, roomTypeId)}>{name}</a>
              ) : (
                <a href={planDetailLink}>{name}</a>
              )}
              {/* https://gitlab.fqsv.net/dp-plus/front/issues/144 */}
              {/* <span className="courseCode">［コースコード］{plan && plan.id}</span> */}
            </div>
            <div className="RecommendPlanBody">
              <div
                className="RecommendPlanText"
                dangerouslySetInnerHTML={{ __html: recommendationPoint }}
              />
              <div className="RecommendPlanIcons">
                {tags && tags.map((t) => {
                  const { id, name, tagGroup } = t;
                  let colorType;
                  if (tagGroup.id === '2') {
                    colorType = 'purple';
                  }
                  if (tagGroup.id === '3') {
                    colorType = 'orange';
                  }

                  return (
                    <div className="tagItem" key={id}>
                      <Tag colorType={colorType}>{name}</Tag>
                    </div>
                  );
                })}
                {mealCondition && (
                  <div className="tagItem">
                    <Tag colorType="orange">{mealCondition}</Tag>
                  </div>
                )}
              </div>
              <div className="RecommendPlanLabel">
                <RoomStateLabel color={roomStateLabelColor}>
                  {roomStateLabelText}
                </RoomStateLabel>
              </div>
            </div>
            <div className="RecommendPlanDetail">
              <div className="icons">
                {tags && tags.map((t) => {
                  const { id, name, tagGroup } = t;
                  let colorType;
                  if (tagGroup.id === '2') {
                    colorType = 'purple';
                  }
                  if (tagGroup.id === '3') {
                    colorType = 'orange';
                  }
                  if (id === FEMALE_TAG_ID) {
                    colorType = 'pink';
                  }

                  return (
                    <div className="tagItem" key={id}>
                      <Tag colorType={colorType}>{name}</Tag>
                    </div>
                  );
                })}
                {mealCondition && (
                  <div className="tagItem">
                    <Tag colorType="orange">{mealCondition}</Tag>
                  </div>
                )}
              </div>
              <div className="detail">
                <PriceBlock priceTotal={totalPrice} adultPrice={pricePerAdult} className={priceClassName} />
                <div className="action">
                  <LinkButton
                    buttonType="cta"
                    onClick={handleButton}
                    disabled={!(selectedOutboundFlight && selectedInboundFlight)}
                  >
                    このプランを選択
                  </LinkButton>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {(handleClickPlanListLink || planListLink) && plans.length > showingPlanLimit && !(showingPlanLimit > SHOWING_PLAN_LIMIT) && (
        <div className="link">
          <Textlink
            onClick={handleClickPlanListLink ? () => handleClickPlanListLink(plans[0].hotel) : undefined}
            to={handleClickPlanListLink ? undefined : planListLink}
            className="planListLink"
          >
            この施設のプラン一覧を見る
          </Textlink>
        </div>
      )}
    </div>
  );

};

RecommendPlanList.defaultProps = {
  plans: [],
};

RecommendPlanList.propTypes = {
  handlePlanSelect: PropTypes.func,
  handleClickPlanDetailLink: PropTypes.func,
  plans: PropTypes.array,
  planListLink: string,
  handleClickPlanListLink: PropTypes.func,
  planStartDate: string,
  maxPlanDisplayNumber: number,
};

export default RecommendPlanList;
